import { GatsbyImage, getImage, ImageDataLike } from 'gatsby-plugin-image';
import React from 'react';
import './CardTrioCard.scss';
import { Eyebrow } from '../Eyebrow/Eyebrow';
import { SmartLink } from '../SmartLink/SmartLink';

export interface CardTrioCard {
    /** Unique id for the card */
    id: string;
    /** Optional image for the card. Should be a reference to the image node's localFile. */
    image?: ImageDataLike;
    /** Alt text for the card image. Required if an image is included. */
    imageAlt?: string;
    /** Title for the card */
    title?: string;
    /** Eyebrow text for the card. This is shown directly below the image */
    eyebrow?: string;
    /** Secondary, smaller text for the card.  */
    description?: string;
    /** Whether or not to render the description as HTML. */
    descriptionIsHtml?: boolean;
    /** Where the user will be navigated if the card is clicked. */
    linkUrl?: string;
    /** The text for the link. This text will not be visible on screen if "news" is true. */
    linkText?: string;
    /** Whether this is a news card. This hides the link URL visually. */
    news?: boolean;
    /** If true, the image is shown to the left of the text instead of above it. */
    horizontal?: boolean;
    /** If true, the card is blue with white text and padded with the intent that it will take up more space. */
    large?: boolean;
    /** Text (intended to be a date) shown at the bottom of the card. */
    date?: string;

    /** Onclick handler */
    onClick?: () => void;
}

export const CardTrioCard: React.FC<{ card: CardTrioCard }> = ({ card }) => {
    const gImage = card.image ? getImage(card.image) : undefined;
    const InnerText = () => (
        <>
            {card.eyebrow && (
                <Eyebrow
                    text={card.eyebrow}
                    bg={!card.large ? `light` : `dark`}
                />
            )}
            {card.title && (
                <h3
                    className={`${
                        !card.large &&
                        (card.image
                            ? `typestack-title-6 with-img`
                            : `typestack-title-5`)
                    } ${card.news ? `news` : ``} ${
                        card.large ? `typestack-title-3` : ``
                    } ${card.date ? `with-date` : ``}`}
                >
                    {card.title}
                </h3>
            )}
            {card.description && !card.descriptionIsHtml && (
                <div className="desc">
                    <p>{card.description}</p>
                </div>
            )}
            {card.description && card.descriptionIsHtml && (
                <div
                    className="desc"
                    dangerouslySetInnerHTML={{ __html: card.description }}
                ></div>
            )}
            {card.date && <p className="desc-date">{card.date}</p>}
        </>
    );

    return (
        <li
            className={`paragraph__card_trio_card ${
                card.horizontal ? `horizontal` : ``
            } ${card.large ? `large` : ``}`}
        >
            {!card.horizontal && <InnerText />}
            {card.horizontal && (
                <div className="text-wrapper">
                    <InnerText />
                </div>
            )}
            {card.linkUrl && (
                <SmartLink
                    url={card.linkUrl}
                    classNames={card.news ? [`news`] : undefined}
                    onClick={card.onClick}
                >
                    {card.linkText}
                </SmartLink>
            )}
            {card.image && (
                <GatsbyImage image={gImage} alt={card.imageAlt ?? ``} />
            )}
        </li>
    );
};
